import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { StatusBar } from "../components/Shell";
import Ico from "../components/Ico";
import { useAppStore } from "../components/AppStore";
import { chores, neighbors, currentUser } from "../mock";
import { ChevronLeft, ShieldCheck, MapPin, PhoneCall, CheckCircle2, Square, CheckSquare, DollarSign, Clock, HeartHandshake } from "lucide-react";

export default function ParentApproval() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { approveJob, declineJob, requests } = useAppStore();
  const chore = chores.find((c) => c.id === id) || chores[0];
  const poster = neighbors[chore.poster];
  const slot = requests[chore.id]?.slot || chore.slots[0];
  const [agree, setAgree] = useState(false);
  const [done, setDone] = useState(false);

  const approve = () => {
    if (!agree) return;
    approveJob(chore.id);
    setDone(true);
  };
  const decline = () => {
    declineJob(chore.id);
    navigate("/home");
  };

  return (
    <div className="gd-screen bg-[#eef1fb]">
      <div className="flex-shrink-0 px-5 pb-6" style={{ background: "linear-gradient(160deg,#57b24a,#3d8f34)", borderBottomLeftRadius: 28, borderBottomRightRadius: 28 }}>
        <StatusBar dark={false} />
        <div className="mt-1 flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="gd-tap grid h-10 w-10 place-items-center rounded-full bg-white/20"><ChevronLeft size={20} color="#fff" /></button>
          <div>
            <div className="flex items-center gap-1.5 text-white"><HeartHandshake size={16} /><span className="text-xs font-bold text-white/80">PARENT APPROVAL</span></div>
            <h1 className="text-xl font-extrabold text-white">Review This Job</h1>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 rounded-2xl bg-white/15 p-3">
          <div className="grid h-11 w-11 place-items-center rounded-full bg-[#f5911e] font-extrabold text-white">{currentUser.initial}</div>
          <div className="text-white">
            <p className="text-xs text-white/75">Request from</p>
            <p className="text-sm font-extrabold">{currentUser.name} (your child)</p>
          </div>
        </div>
      </div>

      <div className="gd-scroll px-4 pb-40 gd-stagger">
        {/* job */}
        <div className="gd-card mt-4 p-4">
          <div className="flex items-center gap-3">
            <div className="grid h-14 w-14 place-items-center rounded-2xl" style={{ background: `${chore.tint}18` }}><Ico name={chore.icon} size={26} color={chore.tint} /></div>
            <div className="flex-1">
              <p className="text-base font-extrabold text-[#1f2a52]">{chore.title}</p>
              <p className="text-xs font-semibold text-[#9aa3c0]">{slot}</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <MiniStat icon={DollarSign} tint="#4fae3f" v={`$${chore.pay}`} l="Pay" />
            <MiniStat icon={Clock} tint="#3b57c9" v={chore.duration} l="Time" />
            <MiniStat icon={MapPin} tint="#f5911e" v={chore.distance} l="Away" />
          </div>
        </div>

        {/* verified poster */}
        <div className="gd-card mt-3 p-4">
          <h3 className="mb-2 text-sm font-extrabold text-[#1f2a52]">Who Posted This Job</h3>
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl font-extrabold text-white" style={{ background: poster.color }}>{poster.initial}</div>
            <div className="flex-1"><p className="text-sm font-extrabold text-[#1f2a52]">{poster.name}</p><p className="text-[11px] font-semibold text-[#9aa3c0]">{poster.jobsPosted} jobs · {poster.rating.toFixed(1)}★</p></div>
          </div>
          <div className="mt-3 rounded-2xl bg-[#eaf6ea] p-3">
            <div className="flex items-center gap-2 text-sm font-extrabold text-[#2e7d27]"><ShieldCheck size={18} /> Background Check Passed</div>
            <div className="mt-2 space-y-1.5">
              {poster.backgroundCheck.items.map((it) => (
                <div key={it} className="flex items-center gap-2 text-[12px] font-medium text-[#3a4468]"><CheckCircle2 size={14} className="text-[#4fae3f]" /> {it}</div>
              ))}
            </div>
            <p className="mt-2 text-[10px] font-semibold text-[#5b7a55]">Cleared by {poster.backgroundCheck.provider} · {poster.backgroundCheck.clearedOn}</p>
          </div>
        </div>

        {/* safety features */}
        <div className="gd-card mt-3 p-4">
          <h3 className="mb-2 text-sm font-extrabold text-[#1f2a52]">Safety Features Active</h3>
          <SafeRow icon={MapPin} tint="#3b57c9" title="Real-Time Tracking" desc="Follow your child's live location during the job." />
          <SafeRow icon={PhoneCall} tint="#e0533d" title="One-Touch SOS" desc="A single tap alerts your preprogrammed contact." />
        </div>

        {/* waiver */}
        <div className="gd-card mt-3 p-4">
          <h3 className="mb-2 text-sm font-extrabold text-[#1f2a52]">Waiver of Liability</h3>
          <div className="max-h-32 overflow-y-auto rounded-2xl bg-[#f7f8fd] p-3 text-[11px] leading-relaxed text-[#5b648a]" style={{ scrollbarWidth: "none" }}>
            By approving this job, I, {currentUser.parent}, parent/guardian of {currentUser.name}, grant permission for my child to
            perform the above task for the verified neighbor listed. I acknowledge that GEN-DO has completed a background
            check on the poster and provides real-time tracking and one-touch emergency contact features. I voluntarily
            accept the risks associated with this activity and release GEN-DO, its affiliates, and the poster from liability
            for any injury, loss, or damage, except in cases of gross negligence. I confirm I have reviewed the job details,
            location, and pay, and I approve my child's participation.
          </div>
          <button onClick={() => setAgree(!agree)} className="mt-3 flex items-start gap-2.5 text-left">
            {agree ? <CheckSquare size={22} className="mt-0.5 flex-shrink-0 text-[#4fae3f]" /> : <Square size={22} className="mt-0.5 flex-shrink-0 text-[#c7cde6]" />}
            <span className="text-[12px] font-semibold text-[#3a4468]">I have read and accept the waiver of liability, and I approve this job for my child.</span>
          </button>
        </div>
      </div>

      {/* CTA */}
      <div className="absolute inset-x-0 bottom-0 bg-white/90 px-5 pb-6 pt-4 backdrop-blur" style={{ boxShadow: "0 -8px 24px rgba(31,42,82,.08)" }}>
        <button onClick={approve} disabled={!agree} className="w-full rounded-2xl py-4 text-base font-extrabold text-white transition-all"
          style={{ background: agree ? "linear-gradient(180deg,#57b24a,#3d8f34)" : "#c7cde6", boxShadow: agree ? "0 12px 24px rgba(61,143,52,.35)" : "none" }}>
          Approve Job & Accept Waiver
        </button>
        <button onClick={decline} className="mt-2 w-full rounded-2xl py-3 font-bold text-[#e0533d]">Decline</button>
      </div>

      {done && (
        <div className="absolute inset-0 z-50 flex items-end bg-black/40">
          <div className="gd-enter w-full rounded-t-[28px] bg-white p-6 pb-8 text-center">
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[#e6e9f5]" />
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#eaf6ea]"><CheckCircle2 size={34} className="text-[#4fae3f]" /></div>
            <h3 className="mt-3 text-xl font-black text-[#1f2a52]">Job Approved!</h3>
            <p className="mt-1 text-sm text-[#5b648a]">{currentUser.name} is cleared to start <b>{chore.title}</b>. Tracking & SOS are active.</p>
            <button onClick={() => navigate(`/task/${chore.id}`)} className="gd-blue-btn mt-5 w-full rounded-2xl py-3.5 font-extrabold">View Job</button>
            <button onClick={() => navigate("/home")} className="mt-2 w-full rounded-2xl py-3 font-bold text-[#5b648a]">Back to Home</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ icon: Icon, tint, v, l }) {
  return (
    <div className="rounded-2xl bg-[#f7f8fd] py-2.5">
      <Icon size={16} color={tint} className="mx-auto" />
      <p className="mt-0.5 text-sm font-black text-[#1f2a52]">{v}</p>
      <p className="text-[9px] font-semibold text-[#9aa3c0]">{l}</p>
    </div>
  );
}
function SafeRow({ icon: Icon, tint, title, desc }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full" style={{ background: `${tint}18` }}><Icon size={18} color={tint} /></div>
      <div><p className="text-[13px] font-bold text-[#1f2a52]">{title}</p><p className="text-[11px] text-[#9aa3c0]">{desc}</p></div>
    </div>
  );
}
